import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CategoryInterface, OpeningHoursInterface } from '../../services/exports/Interfaces';
import WorkScheduleHelpers from '../../services/helpers/WorkScheduleHelpers';
import useGetCompanyMenusApi from '../api/companies/useGetCompanyMenusApi';
import useAppMethods from '../utility/useAppMethods';
import { StoreInterface } from '../../store/types';

export interface CategoryRestriction {
  isRestricted: boolean;
  scheduleLabel: string;
  tooltip: string;
}

const EMPTY: CategoryRestriction = { isRestricted: false, scheduleLabel: '', tooltip: '' };

export default function useCategoryRestriction() {
  const { data: menusData } = useGetCompanyMenusApi();
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { formatScheduleUnit } = useAppMethods();
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:CategoryItem' });

  return useCallback(
    (category_ids?: number[]): CategoryRestriction => {
      if (!category_ids?.length || !menusData?.categories) {
        return EMPTY;
      }

      const scheduledSources = category_ids
        .map((id) => menusData.categories[id])
        .filter((c): c is CategoryInterface => Boolean(c?.schedule));

      if (scheduledSources.length === 0) {
        return EMPTY;
      }

      const scheduleLabel = WorkScheduleHelpers.formatSchedule(
        scheduledSources[0].schedule,
        (item: OpeningHoursInterface) => `${formatScheduleUnit(item.open)} - ${formatScheduleUnit(item.close)}`,
        (item: OpeningHoursInterface) =>
          `${formatScheduleUnit(item.open, { day: true, time: true })} - ${formatScheduleUnit(item.close, { day: true, time: true })}`,
      );

      const isRestricted = scheduledSources.every(
        (c) => !WorkScheduleHelpers.isOpen(c.schedule, cached_order?.scheduled_for),
      );

      return {
        isRestricted,
        scheduleLabel,
        tooltip: isRestricted ? t('restricted_tooltip', { schedule: scheduleLabel }) : '',
      };
    },
    [menusData?.categories, cached_order?.scheduled_for, formatScheduleUnit, t],
  );
}
